import type { SalesResolvePayload, SalesResolveShippingOption, SalesResolveSpecialFee } from "../types/sales";
import { readResolveTaxable, readResolveRequired } from "../utils/readResolveTaxable";

const asNum = (v: unknown) => Number(v || 0);

function mapSavedShipRow(r: Record<string, unknown>): SalesResolveShippingOption {
  const id = String(r.shipping_id ?? r.shipping__id ?? "");
  return {
    shipping_id: id,
    shipping_name: String(r.shipping_name ?? ""),
    shipping_type: String(r.shipping_type ?? ""),
    shipping_amount: asNum(r.shipping_amount),
    shipping_taxable: readResolveTaxable(r, ["shipping_taxable", "taxable"], true),
    shipping_delivery_date: String(r.shipping_delivery_date ?? r.shipping_delivarydate ?? ""),
    shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
    is_customer_pickup: r.is_customer_pickup === true,
  };
}

function mapSavedFeeRow(f: Record<string, unknown>): SalesResolveSpecialFee {
  return {
    specialfee_id: String(f.specialfee_id ?? ""),
    specialfee_name: String(f.specialfee_name ?? ""),
    specialfee_amount: asNum(f.specialfee_amount),
    specialfee_taxable: readResolveTaxable(f, ["specialfee_taxable", "taxable"], true),
    required: readResolveRequired(f, false),
  };
}

export type BuildSalesResolveFromSavedOrderOptions = {
  shippingRows?: unknown[] | null;
  feeRows?: unknown[] | null;
  discountCode?: string;
};

/**
 * Build resolve payload from a saved sales order (view / details tax lifecycle).
 */
export function buildSalesResolvePayloadFromSavedOrder(
  order: Record<string, unknown> | null | undefined,
  options?: BuildSalesResolveFromSavedOrderOptions
): SalesResolvePayload {
  const o = order || {};
  const charges = (o.order_charges as unknown[])?.[0] as Record<string, unknown> | undefined;
  const shipRows =
    (options?.shippingRows as unknown[]) ??
    (charges?.shipping_option as unknown[]) ??
    [];
  const feeRows =
    (options?.feeRows as unknown[]) ??
    (charges?.specialfee as unknown[]) ??
    [];

  const shippingOptions = (shipRows as Record<string, unknown>[]).map(mapSavedShipRow);
  const specialFees = (feeRows as Record<string, unknown>[]).map(mapSavedFeeRow);
  const shippingAmount = shippingOptions.reduce((s, r) => s + asNum(r.shipping_amount), 0);

  const items = Array.isArray(o.items)
    ? (o.items as Record<string, unknown>[]).map((it) => ({
        product_id: String(it.product_id ?? ""),
        variant_id: String(it.variant_id ?? ""),
        product_name: String(it.product_name ?? it.name ?? ""),
        sku: String(it.sku ?? ""),
        quantity: Math.max(1, asNum(it.quantity ?? 1)),
        unit_purchase_price: asNum(it.unit_purchase_price),
        unit_retail_price: asNum(
          it.unit_retail_price ?? it.unit_price ?? it.unit_purchase_price
        ),
        changed_unit_price: asNum(
          it.changed_unit_price ?? it.unit_retail_price ?? it.unit_price ?? 0
        ),
        is_price_overridden: it.is_price_overridden === true,
        inventory_source: String(it.inventory_source ?? "signage"),
        is_taxable: readResolveTaxable(
          (it.item_tax_info as Record<string, unknown> | undefined) ?? undefined,
          ["taxable"],
          readResolveTaxable(it, ["is_taxable", "taxable"], true)
        ),
        is_production: !!it.is_production,
        product_type: String(it.product_type ?? "simple"),
        attributes: (it.attributes as Record<string, string>) ?? {},
        custom_information: String(it.custom_information ?? ""),
      }))
    : [];

  return {
    id: String(o.id ?? o._id ?? ""),
    order_number: String(o.order_number ?? ""),
    seller_org_id: String(o.seller_org_id ?? o.signage_id ?? o.seller_owner_node_id ?? ""),
    seller_org_type: String(o.seller_org_type ?? ""),
    seller_membership_id: String(o.seller_membership_id ?? ""),
    seller_global_user_id: String(o.seller_global_user_id ?? ""),
    buyer_org_id: String(o.buyer_org_id ?? ""),
    buyer_org_type: String(o.buyer_org_type ?? ""),
    buyer_membership_id: String(
      o.buyer_membership_id ?? o.customer_membership_id ?? o.customer_id ?? ""
    ),
    buyer_global_user_id: String(o.buyer_global_user_id ?? o.customer_global_user_id ?? ""),
    brokerage_firm_org_id: (o.brokerage_firm_org_id as string | null) ?? null,
    team_member_org_id: (o.team_member_org_id as string | null) ?? null,
    team_member_membership_id: (o.team_member_membership_id as string | null) ?? null,
    team_member_global_user_id: (o.team_member_global_user_id as string | null) ?? null,
    customer_membership_id: String(o.customer_membership_id ?? o.buyer_membership_id ?? o.customer_id ?? ""),
    customer_global_user_id: String(o.customer_global_user_id ?? o.buyer_global_user_id ?? ""),
    agent_org_id: (o.agent_org_id as string | null) ?? null,
    agent_membership_id: (o.agent_membership_id as string | null) ?? null,
    agent_global_user_id: (o.agent_global_user_id as string | null) ?? null,
    signage_id: String(o.signage_id ?? o.seller_org_id ?? o.seller_owner_node_id ?? ""),
    signage_company_name: String(o.signage_company_name ?? ""),
    buyer_type: String(o.buyer_type ?? ""),
    brokerage_firm_id:
      (o.brokerage_firm_org_id as string | null) ??
      (o.brokerage_firm_id as string | null) ??
      null,
    brokerage_firm_name: String(o.brokerage_firm_name ?? ""),
    team_member_id:
      (o.team_member_membership_id as string | null) ??
      (o.team_member_id as string | null) ??
      null,
    team_member_name: String(o.team_member_name ?? ""),
    office: o.office,
    customer_org_id: String(o.customer_org_id ?? o.buyer_org_id ?? "") || null,
    agent_id:
      (o.agent_membership_id as string | null) ?? (o.agent_id as string | null) ?? null,
    agent_name: String(o.agent_name ?? ""),
    customer_first_name: String(o.customer_first_name ?? ""),
    customer_last_name: String(o.customer_last_name ?? ""),
    customer_email: String(o.customer_email ?? ""),
    customer_phone: String(o.customer_phone ?? ""),
    cart_id: (o.cart_id as string | null) ?? null,
    inventory_source: String(o.inventory_source ?? "signage"),
    discount_code: String(options?.discountCode ?? o.discount_code ?? o.promo_code ?? "").trim(),
    shipping_address: o.shipping_address,
    billing_address: o.billing_address,
    both_address_same: !!o.both_address_same,
    items,
    order_type: "sales",
    shipping_amount: shippingAmount,
    shipping_method: {
      carrier_name: String((o.shipping_method as Record<string, unknown>)?.carrier_name ?? ""),
      service_name: String((o.shipping_method as Record<string, unknown>)?.service_name ?? ""),
      tracking_number: String((o.shipping_method as Record<string, unknown>)?.tracking_number ?? ""),
      cost: asNum((o.shipping_method as Record<string, unknown>)?.cost ?? shippingAmount),
    },
    order_tax_summary: {
      tax_status: String(
        (o.order_tax_summary as Record<string, unknown>)?.tax_status ?? o.tax_status ?? ""
      ),
      tax_inclusive: !!(o.order_tax_summary as Record<string, unknown>)?.tax_inclusive,
    },
    order_charges: [{ shipping_option: shippingOptions, specialfee: specialFees }],
  };
}
